import React, { useState, useEffect } from 'react';
import WalkthroughButton from './WalkthroughButton';
import WatchlistParamsForm from './WatchlistParamsForm';
import WatchlistTable from './WatchlistTable';
import WatchlistGroups from './WatchlistGroups';
import LoadingOverlay from './LoadingOverlay';
import {
  getSavedContracts,
  getWatchlistGroups,
  runBulkWatchlist,
  deleteSavedContract,
  assignContractsToGroup,
  simulateGroupContracts, // ✅ API helper for running group simulations
} from '../services/api';
import { isUsageLimitError, getUsageLimitData, isFeatureAccessError, getFeatureAccessData } from '../utils/usageLimitHelper';
import UsageLimitModal from './UsageLimitModal';
import FeatureAccessModal from './FeatureAccessModal';
import {
  BASELINE_MODE_STORAGE_KEY,
  BASELINE_MODES,
  DEFAULT_BASELINE_MODE,
} from '../utils/watchlistBaseline';

function Watchlist() {
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState({ current: 0, total: 0 });
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);

  // Simulation mode state: 'preset' (default ±% scenarios) or 'custom' (single scenario with sliders)
  const [simulationMode, setSimulationMode] = useState('custom');
  const [customDaysToTarget, setCustomDaysToTarget] = useState(null);
  const [customUnderlyingGain, setCustomUnderlyingGain] = useState(null);

  // Days to Exit mode: 'fixed' (universal days) or 'percent' (% of each contract's expiration)
  const [daysToExitMode, setDaysToExitMode] = useState('fixed');
  const [daysToExitPercent, setDaysToExitPercent] = useState(null);

  // Simulation baseline mode — 'avg_cost' (default) compares simulated premiums
  // to the user's avg cost; 'current' compares to today's market premium.
  // Persisted to localStorage so the choice carries across sessions.
  const [baselineMode, setBaselineMode] = useState(() => {
    try {
      const saved = localStorage.getItem(BASELINE_MODE_STORAGE_KEY);
      if (saved === BASELINE_MODES.AVG_COST || saved === BASELINE_MODES.CURRENT) {
        return saved;
      }
    } catch {
      /* noop */
    }
    return DEFAULT_BASELINE_MODE;
  });
  useEffect(() => {
    try {
      localStorage.setItem(BASELINE_MODE_STORAGE_KEY, baselineMode);
    } catch {
      /* noop */
    }
  }, [baselineMode]);

  // For debug/missing-column checks
  const EXPECTED_COLUMNS = [
    "Ticker", "Option Type", "Strike", "Expiration",
    "Current Premium", "Current Underlying",
    "Number of Contracts", "Average Cost per Contract",
    "Equity Invested", "Days to Target",
    "Volume", "Open Interest",
    "Implied Volatility", "Delta", "Theta", "Gamma", "Vega", "Rho"
  ];

  useEffect(() => {
    let cacheRestored = false;

    // Try to restore cached watchlist data on mount
    try {
      const cachedWatchlist = localStorage.getItem('watchlist_last_data');
      const cachedTimestamp = localStorage.getItem('watchlist_last_data_timestamp');

      if (cachedWatchlist && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp);
        const minutesSinceCache = (Date.now() - timestamp) / (1000 * 60);

        // Only restore if less than 30 minutes old (watchlist updates more frequently)
        if (minutesSinceCache < 30) {
          const parsed = JSON.parse(cachedWatchlist);
          setWatchlistItems(parsed);
          cacheRestored = true;
          console.log('✅ Restored watchlist data from cache (skipping fresh fetch)');
        } else {
          // Clear stale cache
          localStorage.removeItem('watchlist_last_data');
          localStorage.removeItem('watchlist_last_data_timestamp');
          console.log('🗑️ Cleared stale watchlist cache (>30min old)');
        }
      }
    } catch (err) {
      console.error('Error restoring watchlist cache:', err);
    }

    // Only fetch fresh data if cache wasn't restored
    if (!cacheRestored) {
      console.log('📡 No valid cache - fetching fresh data from server');
      fetchSavedContracts();
    }

    // Always fetch groups (they're lightweight)
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Fetch Saved Contracts (with normalization) ---
  const fetchSavedContracts = async () => {
    try {
      const response = await getSavedContracts();
      console.log('📥 API Response (Saved Contracts):', response.data);
      if (response.data?.length > 0) {
        console.log('🔑 Keys in first saved contract:', Object.keys(response.data[0]));
      } else {
        console.warn('⚠ No saved contracts returned.');
      }

      const normalized = normalizeDataForTable(response.data);
      console.log('🔍 Normalized Saved Contracts:', normalized);
      setWatchlistItems(normalized);
      console.log('📊 Updated Watchlist Items (Saved):', normalized);

      // Cache watchlist data in localStorage
      try {
        localStorage.setItem('watchlist_last_data', JSON.stringify(normalized));
        localStorage.setItem('watchlist_last_data_timestamp', Date.now().toString());
        console.log('💾 Cached watchlist data to localStorage');
      } catch (err) {
        console.error('Error caching watchlist data:', err);
      }
    } catch (error) {
      console.error('Error loading saved contracts:', error.response?.data || error.message);
    }
  };

  // --- Fetch Groups ---
  const fetchGroups = async () => {
    try {
      const response = await getWatchlistGroups();
      console.log('📥 API Response (Groups):', response.data);
      setGroups(Array.isArray(response.data) ? response.data : []);
      console.log('📊 Updated Groups:', response.data);
    } catch (error) {
      console.error('Error loading groups:', error.response?.data || error.message);
    }
  };

  // --- Normalizer for saved-contract payloads ---
  const normalizeDataForTable = (data) => {
    console.log(`🔄 Normalizing ${data?.length || 0} rows...`);
    return data.map((item, index) => {
      console.log(`Row ${index} Raw Data:`, item);

      const normalized = {
        "Ticker": item.Ticker ?? item.ticker ?? '',
        "Option Type": item["Option Type"] ?? item.option_type ?? '',
        "Strike": item.Strike ?? item.strike ?? '',
        "Expiration": item.Expiration ?? item.expiration ?? '',
        "Current Premium": item["Current Premium"] ?? item.current_premium ?? 0,
        "Current Underlying": item["Current Underlying"] ?? item.current_underlying_price ?? 0,
        "Number of Contracts": item["Number of Contracts"] ?? item.number_of_contracts ?? 0,
        "Average Cost per Contract": item["Average Cost per Contract"] ?? item.average_cost_per_contract ?? 0,
        "Equity Invested": item["Equity Invested"] ?? item.equity_invested ?? 0,
        "Days to Target": item["Days to Target"] ?? item["Days to Gain"] ?? item.days_to_gain ?? 0,
        "Volume": item.Volume ?? item.volume ?? 0,
        "Open Interest": item["Open Interest"] ?? item.open_interest ?? 0,
        "Implied Volatility": item["Implied Volatility"] ?? item.implied_volatility ?? 0,
        "Delta": item.Delta ?? item.delta ?? 'NA',
        "Theta": item.Theta ?? item.theta ?? 'NA',
        "Gamma": item.Gamma ?? item.gamma ?? 'NA',
        "Vega": item.Vega ?? item.vega ?? 'NA',
        "Rho": item.Rho ?? item.rho ?? 'NA',
      };

      console.log(`Row ${index} Normalized Data:`, normalized);
      const missing = EXPECTED_COLUMNS.filter(col => !(col in normalized));
      if (missing.length > 0) {
        console.warn(`⚠ Row ${index} is missing columns:`, missing);
      }

      return normalized;
    });
  };

  // --- Run Selected Contracts (bypass normalization) ---
  const handleRunSelected = async () => {
    try {
      const response = await runBulkWatchlist({ contract_ids: selectedContracts });
      console.log('📥 API Response (Run Selected):', response.data);

      if (Array.isArray(response.data)) {
        console.log('🔍 Bypassing normalization for simulation—using raw data');
        handleSimulationComplete(response.data);

        // Cache results
        try {
          localStorage.setItem('watchlist_last_data', JSON.stringify(response.data));
          localStorage.setItem('watchlist_last_data_timestamp', Date.now().toString());
          console.log('💾 Cached simulation results to localStorage');
        } catch (err) {
          console.error('Error caching simulation results:', err);
        }
      } else {
        console.error('Unexpected response running selected contracts:', response.data);
        handleSimulationComplete([]);
        alert(`Server returned unexpected response: ${JSON.stringify(response.data)}`);
      }
    } catch (error) {
      console.error('Error running selected contracts:', error.response?.data || error.message);
      handleSimulationComplete([]);
      if (isUsageLimitError(error)) {
        setUsageLimitData(getUsageLimitData(error));
      } else if (isFeatureAccessError(error)) {
        setFeatureAccessData(getFeatureAccessData(error));
      } else {
        alert(`Failed to run selected contracts: ${error.message}`);
      }
    }
  };

  // --- Run a Group (bypass normalization) ---
  const handleRunGroup = async (groupId) => {
    try {
      console.log(`▶ Running group with ID: ${groupId}`);

      // Find the group and count contracts
      const group = groups.find(g => g.id === groupId);
      const contractCount = group?.contracts?.length || 0;

      // Show loading modal
      handleSimulationStart(contractCount);

      const response = await simulateGroupContracts(groupId);
      console.log('📥 API Response (Run Group):', response.data);

      if (Array.isArray(response.data)) {
        console.log('🔍 Bypassing normalization for group simulation—using raw data');
        handleSimulationComplete(response.data);

        // Cache simulation results
        try {
          localStorage.setItem('watchlist_last_data', JSON.stringify(response.data));
          localStorage.setItem('watchlist_last_data_timestamp', Date.now().toString());
          console.log('💾 Cached simulation results to localStorage');
        } catch (err) {
          console.error('Error caching simulation results:', err);
        }
      } else if (response.data.error) {
        console.error('Server error running group:', response.data);
        setIsSimulating(false);
        alert(`Server error: ${response.data.error}`);
      } else {
        console.error('Unexpected response running group:', response.data);
        setIsSimulating(false);
        alert(`Unexpected server response: ${JSON.stringify(response.data)}`);
      }
    } catch (error) {
      console.error('Error running group:', error.response?.data || error.message);
      setIsSimulating(false);
      if (isUsageLimitError(error)) {
        setUsageLimitData(getUsageLimitData(error));
      } else if (isFeatureAccessError(error)) {
        setFeatureAccessData(getFeatureAccessData(error));
      } else {
        alert(`Failed to run group: ${error.message}`);
      }
    }
  };

  // --- Delete Contract ---
  const handleDeleteContract = async (id) => {
    try {
      await deleteSavedContract(id);
      await fetchSavedContracts();
    } catch (error) {
      console.error('Error deleting contract:', error.response?.data || error.message);
      alert(`Failed to delete contract: ${error.message}`);
    }
  };

  // --- Scroll to Results ---
  const scrollToResults = () => {
    setTimeout(() => {
      const tableElement = document.querySelector('[data-watchlist-table]');
      if (tableElement) {
        tableElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure DOM has updated
  };

  // --- Handle Simulation Start ---
  const handleSimulationStart = (contractCount) => {
    setIsSimulating(true);
    setSimulationProgress({ current: 0, total: contractCount });

    // Simulate progress during the API call
    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      if (currentProgress < contractCount) {
        currentProgress++;
        setSimulationProgress(prev => ({ ...prev, current: currentProgress }));
      } else {
        clearInterval(progressInterval);
      }
    }, 1000); // Update every second

    // Store interval ID to clear it if needed
    window.simulationProgressInterval = progressInterval;
  };

  // --- Handle Simulation Complete ---
  const handleSimulationComplete = (results) => {
    // Clear any running progress interval
    if (window.simulationProgressInterval) {
      clearInterval(window.simulationProgressInterval);
      window.simulationProgressInterval = null;
    }

    console.log('✅ Simulation Results:', results);
    console.log('🔍 Bypassing normalization for ad-hoc simulator—using raw data');
    setWatchlistItems(results);
    setIsSimulating(false);
    setSimulationProgress({ current: 0, total: 0 });
    scrollToResults();
  };

  return (
    <div className="p-4 text-text">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold text-brand-white">Watchlist Tool</h2>
        <WalkthroughButton sectionKey="watchlist" />
      </div>

      <WatchlistParamsForm
        groups={groups}
        fetchGroups={fetchGroups}
        fetchSavedContracts={fetchSavedContracts}
        onSimulationStart={handleSimulationStart}
        onSimulationComplete={handleSimulationComplete}
      />

      <WatchlistGroups
        groups={groups}
        onRunGroup={handleRunGroup}
        fetchGroups={fetchGroups}
        selectedContracts={selectedContracts}
        setSelectedContracts={setSelectedContracts}
        handleRunSelected={handleRunSelected}
        handleSimulationStart={handleSimulationStart}
        handleSimulationComplete={handleSimulationComplete}
        baselineMode={baselineMode}
        setBaselineMode={setBaselineMode}
        handleBulkDelete={handleDeleteContract}
        handleBulkAssign={async (selectedGroupIds) => {
          try {
            for (const groupId of selectedGroupIds) {
              await assignContractsToGroup(groupId, selectedContracts);
            }
            alert('Contracts assigned successfully!');
            setSelectedContracts([]);
            await fetchGroups();
            await fetchSavedContracts();
          } catch (error) {
            console.error('Error bulk assigning:', error.response?.data || error.message);
            alert(`Failed to assign contracts: ${error.message}`);
          }
        }}
      />

      <div data-watchlist-table>
        <WatchlistTable
          items={watchlistItems}
          selectedTickers={selectedTickers}
          setSelectedTickers={setSelectedTickers}
          selectedExpirations={selectedExpirations}
          setSelectedExpirations={setSelectedExpirations}
          selectedScenarios={selectedScenarios}
          setSelectedScenarios={setSelectedScenarios}
          simulationMode={simulationMode}
          onSimulationModeChange={setSimulationMode}
          customDaysToTarget={customDaysToTarget}
          customUnderlyingGain={customUnderlyingGain}
          onCustomSettingsChange={(days, gain) => {
            setCustomDaysToTarget(days);
            setCustomUnderlyingGain(gain);
          }}
          daysToExitMode={daysToExitMode}
          setDaysToExitMode={setDaysToExitMode}
          daysToExitPercent={daysToExitPercent}
          setDaysToExitPercent={setDaysToExitPercent}
          setCustomDaysToTarget={setCustomDaysToTarget}
          baselineMode={baselineMode}
          setBaselineMode={setBaselineMode}
        />
      </div>

      <LoadingOverlay
        isVisible={isSimulating}
        currentProgress={simulationProgress.current}
        totalProgress={simulationProgress.total}
        message="Running Watchlist Simulation"
      />

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}
    </div>
  );
}

export default Watchlist;
